import type { DataModelState, DataTypeDto, Site } from '@bk/contracts';
import { AREA_ICONS } from './area-icons';
import { rolesOf } from './RoleView';
import { useState, type ReactNode } from 'react';
import { APP_NAME } from '../../lib/app-name';
import { useLogoOnDark } from '../../lib/brand';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { useAdditionActions } from './Additions';
import { AppMark } from '../../ui';
import { ToConfirm, useSections } from './SectionQuestions';
import { rowInLevel, useLevels, useSiteLogo, visibleIn } from './Livelli';

/** Una voce del menu: `path` dopo `#/t/<azienda>/`. */
interface Item {
  path: string;
  label: string;
  count?: number;
  /** Quante cose aspettano una scelta (es. le sedi proposte, ADR 0141): badge in evidenza. */
  alert?: number;
  /** Sezione preparata con l'ipotesi più ampia (ADR 0195 § 7): si conferma aprendola. */
  pending?: boolean;
  /** Il tipo, un suo attributo o sue voci aggiunti da una lettura e da accettare (ADR 0195 § 6). */
  added?: boolean;
}

/** Un gruppo del menu: si apre e si chiude; `items` vuoto = voce singola che porta a `path`. */
interface Group {
  key: string;
  label: string;
  icon: ReactNode;
  items: Item[];
  path?: string;
}

/** Testo confrontabile: minuscolo e senza accenti. */
const fold = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const byLabel = (a: DataTypeDto, b: DataTypeDto) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' });

/** Icona a tratto, 18px, nel colore del testo. */
function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

const ICONS = {
  dashboard: 'M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  oggi: 'M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9',
  azienda: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M3 21h18M8 8h3M8 12h3M8 16h3',
  mestiere: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  area: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  lavoro: 'M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1M4 6h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1M3 12h18',
  fonti: 'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6',
};

/**
 * Menu laterale dell'azienda (ADR 0112, 0202): le entità vengono dal modello dei dati e stanno nelle aree funzionali della
 * libreria, prima quelle del mestiere e poi quelle comuni; un'area compare solo se l'azienda ha qualcosa lì. Il menu è
 * diverso per ogni azienda e cambia da solo quando il modello cambia. `active` è il percorso della pagina aperta.
 */
export function Sidebar({ active, logo: companyLogo }: { active: string; logo: string | null }) {
  const { tenant, isManager, procedureAreas, setLivello } = useTenant();
  // I due livelli (ADR 0210 § 8): dentro una sede il menu ha le sue sezioni, il suo logo e i suoi colori.
  const levels = useLevels();
  const inSite = !!levels.site;
  const siteLogo = useSiteLogo(levels.site);
  const logo = inSite ? (siteLogo ?? companyLogo) : companyLogo;
  const [choosing, setChoosing] = useState(false);
  const dark = useLogoOnDark(logo);
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const proposals = (sites.data?.data ?? []).filter((x) => x.status === 'da_confermare').length;
  const [search, setSearch] = useState('');
  const sections = useSections();
  // Le voci di una testata (righe della fattura…) stanno nella scheda della testata, non nel menu (ADR 0203).
  const types = (model.data?.data ?? []).filter((x) => x.stato === 'attivo' && !sections.tipi.get(x.chiave)?.parte_di && visibleIn(levels, x.chiave, inSite)).sort(byLabel);
  const entity = (x: DataTypeDto): Item => ({ path: `e/${x.chiave}`, label: x.etichetta, count: x.entita.filter((e) => rowInLevel(levels, e.legami)).length, pending: sections.tipi.get(x.chiave)?.da_confermare, added: hasAdditions(x) });
  const roles = rolesOf(model.data?.data ?? []);
  const areaOf = (x: DataTypeDto) => sections.tipi.get(x.chiave)?.area ?? null;
  const known = new Set((sections.state?.aree ?? []).map((a) => a.chiave));
  // Le aree della libreria con i tipi dell'azienda; le viste per ruolo prima dei tipi. Un'area vuota non compare.
  const areas: Group[] = (sections.state?.aree ?? [])
    .map((a) => {
      const own = types.filter((x) => areaOf(x) === a.chiave);
      const views = a.viste.map((v) => ({ path: `v/${v.ruolo}`, label: v.nome, count: roles.count(v.ruolo) }));
      return { key: `area:${a.chiave}`, label: a.nome, icon: <Icon d={AREA_ICONS[a.icona] ?? (a.mestiere ? ICONS.mestiere : ICONS.area)} />, items: own.length ? [...views, ...own.map(entity)] : [] };
    })
    .filter((g) => g.items.length > 0);
  // I tipi senza un'area della libreria (da un programma, da una lettura, aggiunti dalla persona).
  const others = types.filter((x) => !known.has(areaOf(x) ?? ''));
  const groups: Group[] = [
    { key: 'dashboard', label: 'Dashboard', icon: <Icon d={ICONS.dashboard} />, items: [], path: 'dashboard' },
    { key: 'oggi', label: 'Oggi', icon: <Icon d={ICONS.oggi} />, items: [], path: 'oggi' },
    inSite
      ? {
          key: 'azienda',
          label: 'La sede',
          icon: <Icon d={ICONS.azienda} />,
          items: [
            { path: `sedi/${levels.site!.id}`, label: 'Scheda della sede' },
            { path: 'ruoli', label: 'Ruoli' },
            { path: 'persone', label: 'Persone' },
            ...(isManager ? [{ path: 'sistemi', label: 'Sistemi' }, { path: 'accessi-dati', label: 'Accessi ai dati' }] : []),
          ],
        }
      : {
      key: 'azienda',
      label: 'L’azienda',
      icon: <Icon d={ICONS.azienda} />,
      items: [
        { path: 'impresa', label: 'Impresa' },
        { path: 'organi', label: 'Organi e cariche' },
        // I conti dal bilancio: solo per il titolare e i responsabili (ADR 0132).
        ...(isManager ? [{ path: 'conti', label: 'Conti' }] : []),
        { path: 'sedi', label: 'Sedi', alert: isManager ? proposals : 0 },
        { path: 'ruoli', label: 'Ruoli' },
        { path: 'persone', label: 'Persone' },
        ...(isManager ? [{ path: 'sistemi', label: 'Sistemi' }, { path: 'accessi-dati', label: 'Accessi ai dati' }] : []),
      ],
    },
    ...areas,
    { key: 'altro', label: 'Altro', icon: <Icon d={ICONS.area} />, items: others.map(entity) },
    {
      key: 'lavoro',
      label: 'Lavoro',
      icon: <Icon d={ICONS.lavoro} />,
      items: [
        { path: 'esecutori', label: 'Sistemi e approvazioni' },
        ...(isManager || procedureAreas.length > 0 ? [{ path: 'procedure', label: 'Procedure' }] : []),
        { path: 'processi', label: 'Processi' },
        { path: 'esecuzioni', label: 'Esecuzioni' },
        { path: 'controlli', label: 'Controlli' },
        { path: 'indicatori', label: 'Indicatori' },
        ...(isManager ? [{ path: 'proposte', label: 'Proposte' }] : []),
      ],
    },
    {
      key: 'fonti',
      label: 'Fonti',
      icon: <Icon d={ICONS.fonti} />,
      items: [
        ...(isManager ? [{ path: 'raccolta', label: 'Raccolta dei dati' }] : []),
        { path: 'archivio', label: 'Archivio' },
        { path: 'software', label: 'Software' },
        { path: 'dati/salvati', label: 'Tutti i dati salvati' },
      ],
    },
  ];
  // Il menu parte sempre tutto chiuso (Luca, 2026-10-27): l'area della pagina aperta si vede evidenziata anche chiusa.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const q = fold(search.trim());
  const shown = groups
    .map((g) => ({ ...g, items: q && !fold(g.label).includes(q) ? g.items.filter((i) => fold(i.label).includes(q)) : g.items }))
    .filter((g) => (g.path ? !q || fold(g.label).includes(q) : g.items.length > 0));
  const href = (path: string) => `#/t/${tenant.id}/${path}`;
  const row = 'flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-medium tracking-[0.01em] transition-colors';
  return (
    <nav className="flex h-full flex-col">
      {/* Il logo dell'azienda quando c'è (ADR 0117), altrimenti il marchio del prodotto. */}
      <a href={`#/t/${tenant.id}/dashboard`} className="flex min-h-[4.5rem] items-center gap-2.5 px-5 pb-4 pt-5" title={tenant.name}>
        {logo ? (
          <img src={logo} alt={tenant.name} className={`max-h-10 max-w-full object-contain ${dark ? 'rounded-lg bg-ink px-2 py-1.5' : ''}`} />
        ) : (
          <>
            <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-accent text-white"><AppMark className="h-full w-full" /></span>
            <span className="text-lg font-semibold tracking-tight">{APP_NAME}</span>
          </>
        )}
      </a>
      {levels.multi && (
        <div className="px-3 pb-2">
          {/* Dove si lavora: tutta l'azienda o una sede (ADR 0210 § 8), in evidenza (ADR 0212). */}
          <button
            type="button"
            aria-expanded={choosing}
            onClick={() => setChoosing((v) => !v)}
            className="flex w-full items-center gap-3 rounded-xl border-2 border-accent/40 bg-accent/[0.08] px-3 py-2.5 text-left text-ink transition hover:border-accent/70"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-sm font-semibold text-white">{(levels.site?.name ?? tenant.name).slice(0, 1)}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-medium uppercase tracking-wide text-ink/50">{levels.site ? 'Sei nella sede' : 'Sei in'}</span>
              <span className="block truncate text-sm font-semibold">{levels.site ? levels.site.name : 'Tutta l’azienda'}</span>
            </span>
            <span className="shrink-0 text-xs font-medium text-accent">{choosing ? 'Chiudi' : 'Cambia'}</span>
          </button>
          {choosing && (
            <ul className="mt-1 space-y-0.5 rounded-[10px] border border-black/[0.06] bg-white p-1">
              {[{ id: null as string | null, nome: 'Tutta l’azienda' }, ...levels.sedi.map((x) => ({ id: x.id as string | null, nome: x.nome }))].map((x) => (
                <li key={x.id ?? 'azienda'}>
                  <button
                    type="button"
                    onClick={() => {
                      setLivello(x.id);
                      setChoosing(false);
                      // Ogni livello ha le sue pagine: si riparte dalla sua dashboard.
                      window.location.hash = `/t/${tenant.id}/dashboard`;
                    }}
                    className={`w-full truncate rounded-lg px-2.5 py-2 text-left text-sm ${(levels.site?.id ?? null) === x.id ? 'bg-accent/10 font-medium text-accent' : 'text-ink/75 hover:bg-accent/[0.06]'}`}
                  >
                    {x.nome}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="px-3 pb-2">
        <input
          className="w-full rounded-[10px] border border-black/[0.06] bg-paper px-3 py-2 text-sm outline-none placeholder:text-ink/40 focus:border-accent/40 focus:bg-white"
          placeholder="Cerca nel menu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {shown.map((g) => {
          if (g.path) {
            const current = active === g.path;
            return (
              <a key={g.key} href={href(g.path)} className={`${row} ${current ? 'bg-accent/10 text-accent' : 'text-ink/80 hover:bg-accent/[0.06] hover:text-ink'}`}>
                <span className="text-accent">{g.icon}</span>
                {g.label}
              </a>
            );
          }
          const expanded = !!q || open.has(g.key);
          const hasActive = g.items.some((i) => i.path === active);
          const questions = g.items.filter((i) => i.pending).length;
          return (
            <div key={g.key}>
              <button className={`${row} ${hasActive && !expanded ? 'bg-accent/10 text-accent' : 'text-ink/80 hover:bg-accent/[0.06] hover:text-ink'}`} aria-expanded={expanded} onClick={() => toggle(g.key)}>
                <span className="text-accent">{g.icon}</span>
                <span className="min-w-0 flex-1 truncate">{g.label}</span>
                {/* Le sezioni dell'area con domande a cui rispondere (ADR 0195 § 7), visibili anche ad area chiusa. */}
                {questions > 0 && (
                  <span title={`${questions === 1 ? '1 sezione' : `${questions} sezioni`} con domande a cui rispondere`} className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none text-amber-800">
                    {questions}
                  </span>
                )}
                {!expanded && g.items.some((i) => i.alert) && <span aria-label="Proposte da scegliere" className="h-2 w-2 rounded-full bg-amber-500" />}
                <svg viewBox="0 0 24 24" className={`h-4 w-4 text-ink/35 transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
              {expanded && isManager && g.items.some((i) => i.added) && <AcceptArea types={g.items.filter((i) => i.added && i.path.startsWith('e/')).map((i) => i.path.slice(2))} seenAt={model.dataUpdatedAt} />}
              {expanded && (
                <ul className="mb-1 ml-[22px] mt-0.5 border-l border-accent/15 pl-2">
                  {g.items.map((i) => {
                    const current = active === i.path;
                    return (
                      <li key={i.path}>
                        <a
                          href={href(i.path)}
                          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${current ? 'bg-accent/10 font-medium text-accent' : i.count === 0 ? 'text-ink/40 hover:bg-accent/[0.06]' : 'text-ink/70 hover:bg-accent/[0.06] hover:text-ink'}`}
                        >
                          <span className="min-w-0 flex-1 truncate">{i.label}</span>
                          {i.pending && <ToConfirm small />}
                          {i.added && (
                            <span title="Aggiunte dalle letture da accettare" className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-sky-800">
                              aggiunto
                            </span>
                          )}
                          {!!i.alert && (
                            <span title={i.alert === 1 ? '1 proposta da scegliere' : `${i.alert} proposte da scegliere`} className="rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none text-white">
                              {i.alert.toLocaleString('it-IT')}
                            </span>
                          )}
                          {!!i.count && (
                            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none ${current ? 'bg-accent text-white' : 'bg-accent/10 text-accent'}`}>
                              {i.count.toLocaleString('it-IT')}
                            </span>
                          )}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
        {!shown.length && <p className="px-3 py-2 text-xs text-ink/50">Nessuna voce contiene «{search.trim()}».</p>}
        {model.data && !types.length && !q && (
          <p className="px-3 py-2 text-xs text-ink/50">Le entità dell’azienda compaiono qui quando il sistema ha ricavato il modello dei dati.</p>
        )}
      </div>
    </nav>
  );
}

/** Ciò che una lettura ha aggiunto a un tipo e la persona non ha ancora accettato (ADR 0195 § 6). */
export function hasAdditions(x: DataTypeDto): boolean {
  return (!!x.aggiunto && !x.aggiunto.accettato_il) || x.attributi.some((a) => a.aggiunto && !a.aggiunto.accettato_il) || x.entita.some((e) => !!e.aggiunta);
}

/** «Accetta tutto» di un'area del menu (ADR 0195 § 6): le aggiunte dei suoi tipi che c'erano quando il menu si è caricato. */
function AcceptArea({ types, seenAt }: { types: string[]; seenAt: number }) {
  const { busy, error, accept } = useAdditionActions();
  if (!types.length) return null;
  return (
    <div className="ml-[22px] mt-0.5 pl-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => void accept({ tipi: types, fino: new Date(seenAt).toISOString() })}
        className="rounded-md px-2.5 py-1 text-[12px] font-medium text-sky-800 hover:bg-sky-50 disabled:opacity-50"
      >
        Accetta le aggiunte dell’area ({types.length === 1 ? '1 sezione' : `${types.length} sezioni`})
      </button>
      {error && <p className="px-2.5 text-[11px] text-red-700">{error}</p>}
    </div>
  );
}
